import React from 'react'

import { action, makeObservable, observable, reaction } from 'mobx'
import { inject, observer } from 'mobx-react'

import LogisticsMapView from '~/client/src/shared/components/LogisticsMapView/LogisticsMapView'
import LogisticsMapViewSetUpStore from '~/client/src/shared/components/LogisticsMapView/LogisticsMapViewSetUp.store'
import { runWithMarkupSessionGuard } from '~/client/src/shared/components/LogisticsMapView/MapMarkupSession.store'
import { createMapExportEntry } from '~/client/src/shared/components/MapExport/createMapExportEntry'
import type { IExportWorkflowSource } from '~/client/src/shared/components/MapExport/ExportPreview.store'
import { takeInterruptedExportRun } from '~/client/src/shared/components/MapExport/exportRunSentinel'
import MapExportStore from '~/client/src/shared/components/MapExport/MapExport.store'
import { PHONE_EXPORT_SETTINGS } from '~/client/src/shared/components/MapExport/mapExportSettings'
import MapViewsGallery from '~/client/src/shared/components/SitemapsGallery/MapViewsGallery'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import ICanvasImageCache from '~/client/src/shared/interfaces/ITextboxesCache'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GlobeView from '~/client/src/shared/models/GlobeView'
import SitePermit from '~/client/src/shared/models/Permit'
import Sitemap from '~/client/src/shared/models/Sitemap'
import BasemapsStore from '~/client/src/shared/stores/domain/Basemaps.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MarkupsStore from '~/client/src/shared/stores/domain/Markups.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import TilesetsStore from '~/client/src/shared/stores/domain/Tilesets.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import {
  REPORT_ERROR,
  SAVE_WORKFLOWS_CONFIGURATIONS,
} from '~/client/src/shared/stores/EventStore/eventConstants'
import WorkflowDrawingGuardStore from '~/client/src/shared/stores/ui/WorkflowDrawingGuard.store'
import { toast } from '~/client/src/shared/utils/toast'

import MobileEventStore from '../../../stores/EventStore/MobileEvents.store'
import MobileInitialState from '../../../stores/MobileInitialState'
import MobileCommonStore from '../../../stores/ui/MobileCommon.store'
import MobileLogisticsStore from '../stores/MobileLogistics.store'
import MobileLogisticsFilterStore from '../stores/MobileLogisticsFilter.store'
import MobileLogisticsMapStore from '../stores/MobileLogisticsMap.store'
import CompactLogisticsFilter from './CompactLogsiticsFilter/CompactLogisticsFilter'
import LogisticsFilters from './LogisticsFilters/LogisticsFilters'
import MapExportSheetBoundary from './MapExportSheet/MapExportSheetBoundary'
import { exportHandOff } from './MapExportSheet/shareExportFile'
import ShareMapButton from './MapExportSheet/ShareMapButton'

// Loaded on first open. React.lazy keeps a failed import, so a failure starts a fresh one for
// the next tap.
const loadMapExportSheet = () =>
  React.lazy(() => import('./MapExportSheet/MapExportSheet'))
let MapExportSheet = loadMapExportSheet()

interface IProps {
  logisticsStore: MobileLogisticsStore
  logisticsMapStore: MobileLogisticsMapStore
  openPermitViewForm: (permit: SitePermit) => void
  // Route for a creation the composer cannot finish (see workflowCreationPolicy).
  openWorkflowCreationForm?: (permitTypeId: string) => void
  // Bubble fallback when no enabled type accepts a precise location.
  openFormTypeSelectionForm?: () => void

  sitemapsStore?: SitemapsStore
  basemapsStore?: BasemapsStore
  sitemapItemsStore?: SitemapItemsStore
  locationAttributesStore?: LocationAttributesStore
  logisticsFilterStore: MobileLogisticsFilterStore
  companiesStore?: CompaniesStore
  userProjectsStore?: UserProjectsStore
  projectMembersStore?: ProjectMembersStore
  tagsStore?: TagsStore
  state?: MobileInitialState
  globeViewsStore?: GlobeViewsStore
  eventsStore?: MobileEventStore
  tilesetsStore?: TilesetsStore
  markupsStore?: MarkupsStore
  workflowDrawingGuardStore?: WorkflowDrawingGuardStore
  common?: MobileCommonStore
}
@inject(
  'sitemapsStore',
  'basemapsStore',
  'sitemapItemsStore',
  'locationAttributesStore',
  'tagsStore',
  'state',
  'companiesStore',
  'userProjectsStore',
  'projectMembersStore',
  'globeViewsStore',
  'eventsStore',
  'tilesetsStore',
  'markupsStore',
  'workflowDrawingGuardStore',
  'common',
)
@observer
export default class MobileLogisticsMap extends React.Component<IProps> {
  @observable private localProps: IProps = null
  public readonly store: LogisticsMapViewSetUpStore = null
  private readonly mapExportStore = new MapExportStore({
    persistsSettings: false,
    recordsRuns: true,
    defaults: PHONE_EXPORT_SETTINGS,
  })
  // Whether files can leave is fixed for the session, so it is asked once.
  private readonly handOff = exportHandOff()
  // One element for the map's life, so the gallery's renders don't re-render the whole map.
  private readonly shareMapButton: JSX.Element
  // One object for the map's life, so the open export's observers don't re-render on every host render.
  private readonly workflowSource: IExportWorkflowSource
  private textboxesCache: ICanvasImageCache = {}
  private disposeNavHide: (() => void) | null = null
  private disposeReopenExport: (() => void) | null = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props

    this.store = new LogisticsMapViewSetUpStore(
      props.sitemapsStore,
      props.tilesetsStore,
      props.globeViewsStore,
      props.basemapsStore,
      props.sitemapItemsStore,
      props.locationAttributesStore,
      props.tagsStore,
      props.state,
      props.logisticsStore,
      props.eventsStore,
      undefined,
      props.markupsStore,
    )
    this.workflowSource = {
      logisticsStore: props.logisticsStore,
      logisticsMapStore: props.logisticsMapStore,
    }
    this.shareMapButton = (
      <ShareMapButton
        exportStore={this.mapExportStore}
        setUpStore={this.store}
      />
    )

    const config = props.state.workflowsMapIdsList

    const isGlobeMode = !!config?.[0]?.globeViewId
    if (isGlobeMode) {
      const globe = props.globeViewsStore.byId.get(config?.[0]?.globeViewId)
      this.store.globeViewControlStore.selectGlobe(globe)
    } else {
      const whiteboard = props.sitemapsStore.byId.get(config?.[0]?.sitemapId)
      this.store.selectSitemap(whiteboard)
      this.store.mapBoxViewerStore.setViewportFromAddress()
    }
    makeObservable(this)
  }

  public componentDidMount() {
    // Enable the workflow-markups display layer on the mobile workflow map, same as
    // desktop LogisticsMap — without this the MarkupLayer visibility gate
    // (supportsWorkflowMarkups) stays false and saved markups never show.
    this.store.mapBoxViewerStore.markupPrefs.setWorkflowMarkupsSupported(
      true,
      !!this.props.state.userActiveProjectSettings?.isAdmin,
    )

    // Share map needs somewhere for the file to go; the native app without sharing has none.
    const exportEntry = createMapExportEntry(
      this.store,
      this.props.state,
      proceed => this.runGuarded(proceed),
    )
    this.mapExportStore.setEntry({
      ...exportEntry,
      isAvailable: () => this.handOff !== 'none' && exportEntry.isAvailable(),
    })
    // The phone ran out of memory mid-export and Safari reloaded the page: Share map reopens once
    // the map can export, saying what happened.
    if (takeInterruptedExportRun(this.props.state.activeProject?.id)) {
      this.disposeReopenExport = this.mapExportStore.reopenAfterReload()
    }

    // Hide the nav chrome while creating so the bottom-sheet form and markup
    // toolbar own the screen.
    this.disposeNavHide = reaction(
      () => this.store.isMapFirstCreationActive,
      isCreating => this.setNavChromeHidden(isCreating),
    )
  }

  // localProps was only seeded in the constructor, so every read through it was a
  // mount-time snapshot.
  @action
  public componentDidUpdate(oldProps: IProps) {
    if (this.props !== oldProps) {
      this.localProps = this.props
    }
  }

  public componentWillUnmount() {
    this.disposeReopenExport?.()
    this.mapExportStore.dispose()
    this.disposeNavHide?.()
    this.disposeNavHide = null
    // Never leave the nav hidden if the map unmounts mid-creation.
    this.setNavChromeHidden(false)
    this.store.dispose()
    this.store.mapBoxViewerStore.markupPrefs.setWorkflowMarkupsSupported(false)
    this.textboxesCache = {}
  }

  private setNavChromeHidden(hidden: boolean): void {
    const { common } = this.props
    if (hidden) {
      common?.hideBottomNavBar()
      common?.hideAppHeader()
    } else {
      common?.showBottomNavBar()
      common?.showAppHeader()
    }
  }

  public render() {
    const { state } = this.props
    const { toggleFilters, isFiltersModalOpen } = this.props.logisticsMapStore
    // Declutter the top during workflow creation: hide the filters bar (and, via
    // MapViewsGallery, the view picker) so the map + pin own the space.
    const isCreating = this.store.isMapFirstCreationActive

    return (
      <>
        {!isCreating && (
          <div className="map-filters body-header row full-width absolute z-index-100">
            <LogisticsFilters
              companiesStore={this.props.companiesStore}
              logisticsStore={this.props.logisticsStore}
              toggleFilters={toggleFilters}
              logisticsFilterStore={this.props.logisticsFilterStore}
              state={state}
              userProjectsStore={this.props.userProjectsStore}
              selectedOptionsCount={this.selectedOptionsCount}
            />
          </div>
        )}
        {this.props.logisticsStore.workflowsLoading && (
          <div
            className="absolute full-width full-height row x-center y-center z-index-50"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}
          >
            <Spinner />
          </div>
        )}
        <MapViewsGallery
          mapIds={state.workflowsMapIdsList}
          selectedGlobeViewId={
            this.store.globeViewControlStore.selectedGlobeViewId
          }
          selectedWhiteboardId={this.store.selectedSitemapId}
          renderMapViewComponent={this.renderMapViewComponent}
          eventName={SAVE_WORKFLOWS_CONFIGURATIONS}
          selectGlobe={this.selectGlobeAndCloseModals}
          selectSitemap={this.selectSitemapAndCloseModals}
          mapReturn={this.mapReturn}
          shouldUseFullHeight={false}
          shouldDisableZoom={true}
          mapBoxViewerStore={this.store.mapBoxViewerStore}
          shouldShowCreateNewButton={false}
          hasFiltersBar={true}
          isCreatingWorkflow={isCreating}
        />
        <CompactLogisticsFilter
          store={this.props.logisticsFilterStore}
          onClose={toggleFilters}
          isShown={isFiltersModalOpen}
          selectedOptionsCount={this.selectedOptionsCount}
          companiesStore={this.props.companiesStore}
          projectMembersStore={this.props.projectMembersStore}
          tagsStore={this.props.tagsStore}
        />
        {this.mapExportStore.isOpen && this.handOff !== 'none' && (
          <MapExportSheetBoundary onError={this.onMapExportSheetError}>
            <React.Suspense fallback={null}>
              <MapExportSheet
                handOff={this.handOff}
                exportStore={this.mapExportStore}
                liveSetUpStore={this.store}
                workflowSource={this.workflowSource}
              />
            </React.Suspense>
          </MapExportSheetBoundary>
        )}
      </>
    )
  }

  private renderMapViewComponent = (
    fullscreenTargetRef?: React.RefObject<HTMLElement>,
  ): JSX.Element => {
    const { openPermitViewForm, logisticsStore, logisticsMapStore } =
      this.localProps

    return (
      <div className="sitemap-container full-width full-height">
        <LogisticsMapView
          globe={this.store.globeViewControlStore.selectedGlobeView}
          sitemap={this.store.sitemap}
          store={this.store}
          logisticsStore={logisticsStore}
          textboxesCache={this.textboxesCache}
          openPermit={openPermitViewForm}
          selectGlobe={this.selectGlobeAndCloseModals}
          selectSitemap={this.selectSitemapAndCloseModals}
          shouldRenderSwipeableCards={true}
          mapBoxViewerStore={this.store.mapBoxViewerStore}
          openWorkflowCreationForm={this.localProps.openWorkflowCreationForm}
          openFormTypeSelectionForm={this.localProps.openFormTypeSelectionForm}
          isCompactMode={true}
          logisticsMapStore={logisticsMapStore}
          fullscreenTargetRef={fullscreenTargetRef}
          mapControlsExtra={this.shareMapButton}
        />
      </div>
    )
  }

  private onMapExportSheetError = (
    error: Error,
    info: React.ErrorInfo,
  ): void => {
    MapExportSheet = loadMapExportSheet()
    this.mapExportStore.close()
    toast.error(Localization.translator.mapExport.shareMapFailed)
    this.props.eventsStore.dispatch(
      REPORT_ERROR,
      info.componentStack,
      error,
      'error',
    )
  }

  private get selectedOptionsCount(): number {
    return Object.values(this.props.logisticsFilterStore.filterStoresByTypeMap)
      .map(value => value.selectedOptions.size)
      .reduce((a, c) => a + c)
  }

  @action.bound
  private selectGlobeAndCloseModals(globe: GlobeView, onSwitched?: () => void) {
    this.runGuarded(() => this.store.switchToGlobe(globe, onSwitched))
  }

  @action.bound
  private selectSitemapAndCloseModals(
    sitemap: Sitemap,
    onSwitched?: () => void,
  ) {
    this.runGuarded(() => this.store.switchToSitemap(sitemap, onSwitched))
  }

  private get mapReturn(): { name: string; back: () => void } | null {
    return this.store.mapReturnLink({
      selectGlobe: this.selectGlobeAndCloseModals,
      selectSitemap: this.selectSitemapAndCloseModals,
    })
  }

  // Any map-view pill switch swaps the mapbox stage out from under terra-draw.
  private runGuarded(proceed: () => void): void {
    runWithMarkupSessionGuard(
      this.store.markupSession,
      this.props.workflowDrawingGuardStore,
      proceed,
    )
  }
}
